import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { FaEye } from "react-icons/fa";

const PastRequests = () => {
  const [devicesData, setDevicesData] = useState([]);
  const [statusDialog, setStatusDialog] = useState({ open: false, request: null });
  const theme = useTheme();

  useEffect(() => {
    try {
      const storedDevices = localStorage.getItem("devices_data");
      if (storedDevices) {
        const parsedDevices = JSON.parse(storedDevices);
        const initializedDevices = parsedDevices.map(device => ({
          ...device,
          requests: device.requests || []
        }));
        setDevicesData(initializedDevices);
      }
    } catch (error) {
      console.error("Error loading devices data from local storage:", error);
    }
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'rejected': return 'error';
      case 'in-progress': return 'primary';
      case 'shipped': return 'secondary';
      case 'delivered': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'in-progress': return 'In Progress';
      case 'shipped': return 'Shipped';
      case 'delivered': return 'Delivered';
      default: return 'Unknown';
    }
  };

  
  const StatusDialog = ({ open, request, onClose }) => {
    if (!request) return null;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#202020", color: "#EEEEEE" }}>
          Request Details & Status
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#202020", color: "#EEEEEE" }}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Status: <Chip label={getStatusLabel(request.status)} color={getStatusColor(request.status)} size="small" />
            </Typography>
            
            {request.reason && (
              <Typography variant="body2" sx={{ color: "#F87171", mb: 2 }}>
                Reason: {request.reason}
              </Typography>
            )}

            {request.trackingId && (
              <Typography variant="body2" sx={{ color: "#10B981", mb: 2 }}>
                Tracking ID: {request.trackingId}
              </Typography>
            )}

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Requested Items:
            </Typography>
            
            {request.motor && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Motor: {request.motor.kv || ''} {request.motor.size || ''} {request.motor.brand || ''} (x{request.motor.quantity || 1})
              </Typography>
            )}
            
            {request.propeller && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Propeller: {request.propeller.size || ''} {request.propeller.material || ''} (x{request.propeller.quantity || 1})
              </Typography>
            )}
            
            {request.esc && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                • ESC: {request.esc.amperage || ''} {request.esc.cells || ''} {request.esc.protocol || ''} (x{request.esc.quantity || 1})
              </Typography>
            )}
            
            {request.battery && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Battery: {request.battery.capacity || ''} {request.battery.cells || ''} {request.battery.cRating || ''} (x{request.battery.quantity || 1})
              </Typography>
            )}
            
            {request.flightController && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Flight Controller: {request.flightController.mcu || ''} {request.flightController.gyro || ''} (x{request.flightController.quantity || 1})
              </Typography>
            )}

            {request.description && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Description:
                </Typography>
                <Typography variant="body2">
                  {request.description}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" sx={{ mt: 2, color: "#9CA3AF" }}>
              Requested on: {new Date(request.timestamp).toLocaleDateString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#202020" }}>
          <Button onClick={onClose} sx={{ color: "#EEEEEE" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  
  const allRequestsForTable = devicesData.flatMap(device =>
    device.requests.map(req => ({
      ...req,
      deviceName: device.deviceName
    }))
  ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: theme.spacing(3),
        color: "#EEEEEE",
      }}
    >
      <Box mb={3}>
        <Typography
          variant="h4"
          sx={{ fontSize: "1.5rem", color: theme.palette.text.primary, fontWeight: 500 }}
        >
          Past Requests
        </Typography>
      </Box>

      {/* Past Requests Table */}
      {allRequestsForTable.length > 0 ? (
        <Box
          sx={{
            backgroundColor: "#202020",
            padding: theme.spacing(3),
            borderRadius: theme.spacing(1.5),
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
            marginBottom: theme.spacing(4),
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: "1.25rem",
              fontWeight: 300,
              color: "#EEEEEE",
              borderBottom: "1px solid #4B5563",
              pb: 1,
              mb: 2,
            }}
          >
            Your Request History
          </Typography>

          <Box sx={{ overflowX: "auto" }}>
            <TableContainer>
              <Table sx={{ minWidth: "100%" }}>
                <TableHead sx={{ backgroundColor: "#1D1D1D" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "12px 24px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: "1px solid #374151",
                      }}
                    >
                      Item (Details, Qty)
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 24px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: "1px solid #374151",
                      }}
                    >
                      Requested For
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 24px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: "1px solid #374151",
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 24px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: "1px solid #374151",
                      }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 24px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: "1px solid #374151",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: "#202020" }}>
                  {allRequestsForTable.map((request) => (
                    <TableRow
                      key={request.id}
                      sx={{ backgroundColor: "#202020" }}
                    >
                      <TableCell
                        sx={{
                          padding: "16px 24px",
                          fontSize: "0.875rem",
                          color: "#EEEEEE",
                          borderBottom: "1px solid #374151",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {request.motor && `Motor: ${request.motor.kv || ''} ${request.motor.size || ''} ${request.motor.brand || ''} (x${request.motor.quantity || 1})`}
                        {request.propeller && <br />}
                        {request.propeller && `Propeller: ${request.propeller.size || ''} ${request.propeller.material || ''} (x${request.propeller.quantity || 1})`}
                        {request.esc && <br />}
                        {request.esc && `ESC: ${request.esc.amperage || ''} ${request.esc.cells || ''} ${request.esc.protocol || ''} (x${request.esc.quantity || 1})`}
                        {request.battery && <br />}
                        {request.battery && `Battery: ${request.battery.capacity || ''} ${request.battery.cells || ''} ${request.battery.cRating || ''} (x${request.battery.quantity || 1})`}
                        {request.flightController && <br />}
                        {request.flightController && `FC: ${request.flightController.mcu || ''} ${request.flightController.gyro || ''} (x${request.flightController.quantity || 1})`}
                        {!request.motor && !request.propeller && !request.esc && !request.battery && !request.flightController && "N/A"}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 24px",
                          fontSize: "0.875rem",
                          color: "#EEEEEE",
                          borderBottom: "1px solid #374151",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {request.deviceName || "N/A"}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 24px",
                          fontSize: "0.875rem",
                          color: "#EEEEEE",
                          borderBottom: "1px solid #374151",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Chip 
                          label={getStatusLabel(request.status)} 
                          color={getStatusColor(request.status)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 24px",
                          fontSize: "0.875rem",
                          color: "#EEEEEE",
                          borderBottom: "1px solid #374151",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {new Date(request.timestamp).toLocaleDateString()}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 24px",
                          fontSize: "0.875rem",
                          color: "#EEEEEE",
                          borderBottom: "1px solid #374151",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => setStatusDialog({ open: true, request })}
                          sx={{ color: "#3B82F6" }}
                        >
                          <FaEye />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: "#202020",
            padding: theme.spacing(4),
            borderRadius: theme.spacing(1.5),
            textAlign: "center",
            color: "#9CA3AF",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            No Past Requests
          </Typography>
          <Typography variant="body2">
            You haven't submitted any requests yet. Go to the Requests page to submit your first request.
          </Typography>
        </Box>
      )}

      {/* Status Dialog */}
      <StatusDialog
        open={statusDialog.open}
        request={statusDialog.request}
        onClose={() => setStatusDialog({ open: false, request: null })}
      />
    </Box>
  );
};

export default PastRequests;